/**
 * 경계 관련 서비스
 */
import carbonApi from "../api";
import { unwrapResponse, extractArrayData } from "../utils/api.js";

/**
 * 조직 경계 목록 조회
 * @param {string} orgCode - 조직 코드
 * @param {string} status - 상태 (active, inactive 등)
 * @returns {Promise<Array>} 조직 경계 목록
 */
export async function fetchOrgBoundaries(orgCode, status = "active") {
    if (!orgCode) {
        throw new Error("orgCode는 필수입니다.");
    }

    const response = await carbonApi.orgBoundary.getOrgBoundaries(orgCode, status);
    const data = unwrapResponse(response, { defaultMessage: "조직 경계 목록을 불러오지 못했습니다." });
    const boundaries = extractArrayData(data, ["boundaries", "items", "data"]);
    
    if (!Array.isArray(boundaries) || boundaries.length === 0) {
        return [];
    }
    
    // API 응답 필드명 통일: orgBoundaryIdx -> boundaryIdx, orgBoundaryName -> boundaryName
    return boundaries.map(boundary => {
        // 원본 필드명도 유지하면서 통일된 필드명 추가
        return {
            ...boundary,
            boundaryIdx: boundary.boundaryIdx ?? boundary.orgBoundaryIdx ?? null,
            boundaryName: boundary.boundaryName ?? boundary.orgBoundaryName ?? boundary.name ?? null,
        };
    });
}

/**
 * 조직 경계 상세 조회
 * @param {string|number} boundaryIdx - 경계 인덱스
 * @returns {Promise<Object>} 조직 경계 정보
 */
export async function fetchOrgBoundaryDetail(boundaryIdx) {
    if (!boundaryIdx) {
        throw new Error("boundaryIdx는 필수입니다.");
    }

    const response = await carbonApi.orgBoundary.getOrgBoundary(boundaryIdx);
    return unwrapResponse(response, { defaultMessage: "조직 경계 정보를 불러오지 못했습니다." });
}

/**
 * 운영 경계 목록 조회
 * @param {string} orgCode - 조직 코드
 * @returns {Promise<Array>} 운영 경계 목록
 */
export async function fetchOperBoundaries(orgCode) {
    if (!orgCode) {
        throw new Error("orgCode는 필수입니다.");
    }

    const response = await carbonApi.operBoundary.getOperBoundaries(orgCode);
    const data = unwrapResponse(response, { defaultMessage: "운영 경계 목록을 불러오지 못했습니다." });
    return extractArrayData(data, ["boundaries", "items", "data"]);
}

/**
 * 운영 경계 상세 조회
 * @param {string|number} operBoundaryIdx - 운영 경계 인덱스
 * @returns {Promise<Object>} 운영 경계 정보
 */
export async function fetchOperBoundaryDetail(operBoundaryIdx) {
    if (!operBoundaryIdx) {
        throw new Error("operBoundaryIdx는 필수입니다.");
    }

    const response = await carbonApi.operBoundary.getOperBoundary(operBoundaryIdx);
    return unwrapResponse(response, { defaultMessage: "운영 경계 정보를 불러오지 못했습니다." });
}

/**
 * 조직과 경계 정보를 함께 조회
 * @param {string} orgCode - 조직 코드
 * @returns {Promise<Object>} 조직 및 경계 정보
 */
export async function fetchOrgContext(orgCode) {
    const [orgBoundaryRes, operBoundaryRes] = await Promise.all([
        fetchOrgBoundaries(orgCode, "active"),
        fetchOperBoundaries(orgCode),
    ]);

    return {
        orgBoundaries: orgBoundaryRes,
        operBoundaries: operBoundaryRes,
    };
}
